import { ComponentType, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { ConfirmDiscardPlanDialog } from './components/discard-plan-dialog';
import { Duration } from './steps/duration';
import { RecommendationReview } from './steps/recommendation-review';
import { SkillSelection } from './steps/skills-selection';
import type { CreatePlanWizardValues } from './types';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { PageSkeleton } from '@/components/shared/page-skeleton';
import { useNotification } from '@/context/notification';
import { useWizard } from '@/context/wizard';
import { Wizard } from '@/context/wizard/components/wizard';
import { WizardProvider } from '@/context/wizard/context';
import { clientKeys } from '@/data/@clients/client-keys';
import { usePlanShow } from '@/data/idp/plans';
import {
  useDeleteProfileBasedRecommendation,
  useProfileBasedRecommendationShow,
} from '@/data/idp/profile-based-recommendations';
import { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';
import { Feedback } from './steps/feedback';
import { NewFeedback } from './steps/new-feedback';
import { ProcessingFeedback } from './steps/processing-feedback';
import { useShowUserFeedback } from '@/data/guidance/feedback';

const steps: Record<RecommendationPlanIds, { component: ComponentType }> = {
  [RecommendationPlanIds.Duration]: { component: Duration },
  [RecommendationPlanIds.Feedback]: { component: Feedback },
  [RecommendationPlanIds.NewFeedback]: { component: NewFeedback },
  [RecommendationPlanIds.ProcessingFeedback]: { component: ProcessingFeedback },
  [RecommendationPlanIds.SkillSelection]: { component: SkillSelection },
  [RecommendationPlanIds.RecommendationReview]: {
    component: RecommendationReview,
  },
};

const CreatePlanContent = () => {
  const { values } = useWizard<CreatePlanWizardValues>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const { mutate, isPending } = useDeleteProfileBasedRecommendation();
  const { toast } = useNotification();

  const handleBackToPlanList = async () => {
    await queryClient.refetchQueries({ queryKey: [clientKeys.usePlansIndex] });
    await queryClient.invalidateQueries({
      queryKey: [clientKeys.useProfileBasedRecommendationShow],
    });
    navigate('/pdi/planos');
  };

  const handleDiscardPlan = () => {
    mutate(values.id!, {
      onSuccess: handleBackToPlanList,
      onError: () => {
        toast({
          content: 'Não foi possível descartar o plano. Tente novamente.',
          type: 'error',
        });
      },
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Novo plano"
        backNavigationText="Voltar"
        handleBackNavigation={() => setDiscardDialogOpen(true)}
      />
      <Wizard steps={steps} />
      {discardDialogOpen && (
        <ConfirmDiscardPlanDialog
          onCancel={() => setDiscardDialogOpen(false)}
          onContinueLater={handleBackToPlanList}
          onConfirm={handleDiscardPlan}
          isLoading={isPending}
        />
      )}
    </PageContainer>
  );
};

export const CreatePlan = () => {
  const { id } = useParams();
  const { plan, isPending: isPlanPending } = usePlanShow(id!);
  const { userFeedback } = useShowUserFeedback();
  const { data, isPending: isRecommendationPending } =
    useProfileBasedRecommendationShow({
      id: plan?.profile_based_recommendation?.id || '',
    });

  // Capture userFeedback at mount time to avoid resetting wizard state when
  // feedback processing completes (which updates userFeedback). The wizard
  // should only reinitialize when the plan/recommendation itself changes.
  const initialUserFeedback = useMemo(
    () => userFeedback,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan?.id]
  );

  const initialState = useMemo(
    () => ({
      currentStepId: data?.current_step ?? RecommendationPlanIds.Duration,
      values: {
        id: plan?.profile_based_recommendation?.id,
        planId: plan?.id,
        [RecommendationPlanIds.Duration]: data?.duration_in_months ?? null,
        [RecommendationPlanIds.Feedback]: {
          currentFeedback: initialUserFeedback || null,
          option: null,
        },
        [RecommendationPlanIds.NewFeedback]: null,
        [RecommendationPlanIds.ProcessingFeedback]: null,
        [RecommendationPlanIds.SkillSelection]: data?.developing_skills || null,
        [RecommendationPlanIds.RecommendationReview]: {
          recommended_activities: data?.recommended_activities || null,
          recommended_plan_description:
            data?.recommended_plan_description || '',
        },
      },
    }),
    [
      data?.current_step,
      data?.duration_in_months,
      data?.developing_skills,
      data?.recommended_activities,
      data?.recommended_plan_description,
      plan?.profile_based_recommendation?.id,
      plan?.id,
      initialUserFeedback,
    ]
  );

  if (isPlanPending || isRecommendationPending) return <PageSkeleton />;

  return (
    <WizardProvider initialState={initialState}>
      <CreatePlanContent />
    </WizardProvider>
  );
};
